/**
 * Shared App Context and Utilities
 * This file exports shared contexts and utilities for use across pages
 */
import { createContext, useContext } from "react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth and Language Contexts - created here, provided by App.js
export const AuthContext = createContext(null);
export const LanguageContext = createContext(null);

// Hook to use Auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Hook to use Language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

/**
 * Check if a user has active subscription access
 * @param {Object} user - The user object
 * @returns {{ hasAccess: boolean, status: string, reason: string }}
 */
export const checkSubscriptionAccess = (user) => {
  if (!user) {
    return { hasAccess: false, status: 'unauthenticated', reason: 'Not logged in' };
  }
  
  // Admin always has full access
  if (user.is_admin || user.role === 'admin') {
    return { hasAccess: true, status: 'admin', reason: 'Admin access' };
  }
  
  // Clients don't need subscription
  if (user.role === 'client') {
    return { hasAccess: true, status: 'client', reason: 'Clients have free access' };
  }
  
  // For providers, check subscription status
  const subscription = user.subscription;
  const expiresAt = user.subscription_expires ? new Date(user.subscription_expires) : null;
  const now = new Date();
  
  // Active paid subscription
  if (subscription === 'monthly' || subscription === 'yearly' || subscription === 'annual') {
    if (expiresAt && expiresAt > now) {
      return { hasAccess: true, status: 'active', reason: 'Active subscription' };
    } else {
      return { hasAccess: false, status: 'expired', reason: 'Subscription expired' };
    }
  }
  
  // Active promo subscription
  if (subscription === 'promo') {
    if (expiresAt && expiresAt > now) {
      return { hasAccess: true, status: 'active', reason: 'Active promo subscription' };
    } else {
      return { hasAccess: false, status: 'expired_trial', reason: 'Promo period expired' };
    }
  }
  
  // Trial subscription
  if (subscription === 'trial') {
    if (expiresAt && expiresAt > now) {
      return { hasAccess: true, status: 'trial', reason: 'Active trial' };
    } else {
      return { hasAccess: false, status: 'expired_trial', reason: 'Trial expired' };
    }
  }
  
  // Pending renewal (auto-renew failed or expired)
  if (subscription === 'pending_renewal') {
    return { hasAccess: false, status: 'pending_renewal', reason: 'Subscription renewal pending' };
  }
  
  // No subscription (new user, never paid)
  return { hasAccess: false, status: 'pending', reason: 'No active subscription' };
};

// Swiss Cantons
export const SWISS_CANTONS = [
  { code: "AG", name: "Aargau" },
  { code: "AI", name: "Appenzell Innerrhoden" },
  { code: "AR", name: "Appenzell Ausserrhoden" },
  { code: "BE", name: "Bern" },
  { code: "BL", name: "Basel-Landschaft" },
  { code: "BS", name: "Basel-Stadt" },
  { code: "FR", name: "Fribourg" },
  { code: "GE", name: "Genève" },
  { code: "GL", name: "Glarus" },
  { code: "GR", name: "Graubünden" },
  { code: "JU", name: "Jura" },
  { code: "LU", name: "Luzern" },
  { code: "NE", name: "Neuchâtel" },
  { code: "NW", name: "Nidwalden" },
  { code: "OW", name: "Obwalden" },
  { code: "SG", name: "St. Gallen" },
  { code: "SH", name: "Schaffhausen" },
  { code: "SO", name: "Solothurn" },
  { code: "SZ", name: "Schwyz" },
  { code: "TG", name: "Thurgau" },
  { code: "TI", name: "Ticino" },
  { code: "UR", name: "Uri" },
  { code: "VD", name: "Vaud" },
  { code: "VS", name: "Valais" },
  { code: "ZG", name: "Zug" },
  { code: "ZH", name: "Zürich" }
];
